import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react'
import LoadingScreen from './components/ui/LoadingScreen'
import MobileOnlyScreen from './components/ui/MobileOnlyScreen'
import Logo from './components/ui/Logo'
import Pointer from './components/ui/Pointer'
import ThreeSixty from './components/ui/360'
// Import custom hotspot positions
import { CUSTOM_HOTSPOT_POSITIONS } from './components/data/HotspotPositions'
// Import car data for metalness lookup
import { bmwX3Data } from './components/data/CarData'
// Import GTM tracking
import { trackViewToggle, trackHeaterToggle } from './utils/gtmEvents'

// Lazy load heavy components
const Scene = lazy(() => import('./components/scene/Scene'))
const Trim = lazy(() => import('./components/ui/Trim'))
const ToggleButton = lazy(() => import('./components/ui/ToggleButton'))
const ColorButton = lazy(() => import('./components/ui/ColorButton'))
const BackgroundCards = lazy(() => import('./components/ui/BackgroundCards'))
const CargoCards = lazy(() => import('./components/ui/CargoCards'))
const Sliders = lazy(() => import('./components/ui/Sliders'))
const Popup = lazy(() => import('./components/ui/Popup'))
const CallipersToggle = lazy(() => import('./components/ui/CallipersToggle'))

function App() {
    // Check if device is mobile (tablet or phone)
    // Start with null to prevent flash before detection
    const [isMobile, setIsMobile] = useState(null);

    const [loading, setLoading] = useState(true);
    const [showTrim, setShowTrim] = useState(false);
    const [showScene, setShowScene] = useState(false);
    const [selectedTrim, setSelectedTrim] = useState(null);
    const [modelLoaded, setModelLoaded] = useState(false);
    const [bloomPrewarmingComplete, setBloomPrewarmingComplete] = useState(false);

    // Use refs to track state immediately without waiting for re-render
    const modelLoadedRef = useRef(false);
    const bloomPrewarmingCompleteRef = useRef(false);
    const loadingRef = useRef(true);
    const loadingTimeoutRef = useRef(null); // Track timeout for cleanup
    const isMountedRef = useRef(true); // Track component mount state
    const timeoutsRef = useRef([]); // Track all timeouts for cleanup
    const [show360, setShow360] = useState(false);
    const [showPointer, setShowPointer] = useState(false);
    const [userInteracted, setUserInteracted] = useState(false);
    const [promptsComplete, setPromptsComplete] = useState(false); // Track when 360/pointer sequence is fully done
    const [headlightsOn, setHeadlightsOn] = useState(false);
    const [activeView, setActiveView] = useState(0);
    const [materialChangeFunction, setMaterialChangeFunction] = useState(null);
    const [screenTextureUpdateFunction, setScreenTextureUpdateFunction] = useState(null);
    const [interiorMode, setInteriorMode] = useState(false);
    const [cameraAnimating, setCameraAnimating] = useState(false);
    const [interiorColorApplied, setInteriorColorApplied] = useState(false); // Track if initial interior color was applied
    // Track if models are preloaded during initial loading
    const [modelsPreloaded, setModelsPreloaded] = useState(false);

    // Color persistence state - moved from ColorButton to persist across hotspot navigation
    const [selectedExteriorColor, setSelectedExteriorColor] = useState(null);
    const [selectedInteriorColor, setSelectedInteriorColor] = useState(null);

    // Slider data from Scene component
    const [sliderData, setSliderData] = useState({
        activeHotspot: 'none',
        cameraAnimating: false,
        interiorMode: false,
        sliderChangeHandler: null
    });

    // Heater toggle state for heat waves control
    const [isHeaterToggleActive, setIsHeaterToggleActive] = useState(false);

    // State for popup expansion
    const [isPopupExpanded, setIsPopupExpanded] = useState(false);

    // State for caliper color prompt visibility
    const [showCaliperColorPrompt, setShowCaliperColorPrompt] = useState(false);

    // State for showing color selection text
    const [showColorSelectText, setShowColorSelectText] = useState(false);

    // Helper function to add tracked timeout
    const addTrackedTimeout = useCallback((callback, delay) => {
        if (!isMountedRef.current) return null;
        const id = setTimeout(() => {
            if (isMountedRef.current) {
                callback();
            }
        }, delay);
        timeoutsRef.current.push(id);
        return id;
    }, []);

    // Cleanup function for all timeouts
    const clearAllTimeouts = useCallback(() => {
        timeoutsRef.current.forEach(id => clearTimeout(id));
        timeoutsRef.current = [];
    }, []);

    // Track component mount/unmount
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            clearAllTimeouts();
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
            }
        };
    }, [clearAllTimeouts]);

    // Read URL parameters for pre-selected colors and trim (from AR experience)
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const exteriorParam = urlParams.get('exterior'); // Color name like "Alpine White"
        const interiorParam = urlParams.get('interior'); // Color key like "black", "beige", "brown"
        const trimParam = urlParams.get('trim'); // Trim name like "msport" or "classic"

        if (exteriorParam || interiorParam || trimParam) {
            // Import helper functions to convert to hex
            import('./utils/bmwApi').then(({ getExteriorColorHex, getInteriorColorHex }) => {
                const preferences = {};

                if (exteriorParam) {
                    const exteriorHex = getExteriorColorHex(exteriorParam);
                    preferences.exterior = {
                        colorCode: exteriorHex,
                        name: exteriorParam
                    };
                }

                if (interiorParam) {
                    const interiorHex = getInteriorColorHex(interiorParam);
                    preferences.interior = {
                        colorCode: interiorHex,
                        name: interiorParam
                    };
                }

                // Store in window for later use
                window.urlColorPreferences = preferences;
            });

            // Handle trim parameter - auto-select the trim
            if (trimParam) {
                const trimLower = trimParam.toLowerCase();

                // Define trim data matching Trim.jsx structure
                const trimData = {
                    classic: {
                        id: 0,
                        name: "Sport - Classic BMW Design",
                        modelFile: "BMW.glb",
                        carDataKey: "Classic BMW Design",
                        trimType: "classic",
                        visibleNode: "BASE_PARTS",
                        hiddenNode: "PRO_PARTS"
                    },
                    msport: {
                        id: 1,
                        name: "M Sport - Motorsport Form",
                        modelFile: "BMW.glb",
                        carDataKey: "M Sport - Motorsport Form",
                        trimType: "msport",
                        visibleNode: "PRO_PARTS",
                        hiddenNode: "BASE_PARTS"
                    }
                };

                // Select the trim based on URL parameter
                const selectedTrimData = trimData[trimLower] || trimData.classic;

                // Auto-select trim and skip trim selection screen
                setSelectedTrim(selectedTrimData);
                setShowTrim(false);
                setShowScene(true);
            }
        }
    }, []);

    // Check if device is mobile on mount (ONLY - no resize listener)
    useEffect(() => {
        const checkMobile = () => {
            const userAgent = navigator.userAgent || navigator.vendor || window.opera;

            // Check for mobile/tablet user agents (iOS, Android, tablets)
            const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());

            // Check if device has touch capability
            const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

            // CRITICAL: Only consider it mobile if BOTH conditions are met:
            // 1. Has mobile/tablet user agent AND
            // 2. Has touch capability
            // This prevents desktop browsers from being detected as mobile even when resized
            const isMobileDevice = isMobileUA && hasTouch;

            setIsMobile(isMobileDevice);
        };

        // Only check once on mount - don't listen to resize events
        checkMobile();
    }, []);



    const handleToggle = useCallback((viewId) => {
        // Track view toggle
        trackViewToggle(viewId === 1 ? 'interior' : 'exterior');

        setActiveView(viewId);
        const newInteriorMode = viewId === 1;
        setInteriorMode(newInteriorMode);
    }, []);

    const handleModelLoaded = useCallback(() => {
        if (!isMountedRef.current) return;
        modelLoadedRef.current = true;
        setModelLoaded(true);

        // Multiple checks for mobile reliability
        checkAndHideLoading();
        addTrackedTimeout(checkAndHideLoading, 100);  // Mobile safety check
        addTrackedTimeout(checkAndHideLoading, 300);  // Additional mobile safety
    }, []);

    const handleBloomPrewarmingComplete = useCallback(() => {
        if (!isMountedRef.current) return;
        bloomPrewarmingCompleteRef.current = true;
        setBloomPrewarmingComplete(true);

        // Multiple checks for mobile reliability
        checkAndHideLoading();
        addTrackedTimeout(checkAndHideLoading, 100);  // Mobile safety check
        addTrackedTimeout(checkAndHideLoading, 300);  // Additional mobile safety
    }, []);

    // Check if both conditions are met and hide loading immediately
    const checkAndHideLoading = useCallback(() => {
        if (!isMountedRef.current) return;
        // Check if both model and bloom are ready (using refs for immediate values)
        const bothReady = modelLoadedRef.current && bloomPrewarmingCompleteRef.current;
        const shouldHide = bothReady && loadingRef.current;

        if (shouldHide) {

            // Clear the timeout since we're hiding successfully
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
                loadingTimeoutRef.current = null;
            }

            loadingRef.current = false;
            setLoading(false);

            // Force a second update for mobile devices (they sometimes miss the first)
            requestAnimationFrame(() => {
                if (isMountedRef.current && bothReady) {
                    setLoading(false);
                }
            });
        } else {
            console.log('Conditions check');
        }
    }, []);

    // Expose global function for automatic interior toggle
    useEffect(() => {
        window.autoToggleToInterior = (viewId = 1) => {
            handleToggle(viewId); // Switch to specified view (0 = exterior, 1 = interior)
        };

        return () => {
            delete window.autoToggleToInterior;
        };
    }, [handleToggle]);

    const handleMaterialChangeReady = (changeFunction) => {
        setMaterialChangeFunction(() => changeFunction);
    };

    const handleScreenTextureUpdateReady = (updateFunction) => {
        setScreenTextureUpdateFunction(() => updateFunction);
    };

    // Handle caliper color toggle (red or blue)
    const handleCaliperToggle = (index) => {
        if (!materialChangeFunction) return;

        // Hide the color select text when toggle is used
        setShowColorSelectText(false);

        // index 0 = Red (#FF0000), index 1 = Blue (#0066B1)
        const caliperColor = index === 0 ? '#FF0000' : '#0066B1';

        const materialInfo = {
            materials: [
                { materialName: 'EXT_METAL_RED_ROUGH', colorCode: caliperColor }
            ],
            materialType: 'exterior'
        };

        materialChangeFunction(materialInfo);
    };

    // FORCE interior color application AFTER model loads - ONLY ONCE
    // Also apply URL color preferences if they exist
    useEffect(() => {
        if (materialChangeFunction && modelLoaded && !interiorColorApplied) {
            // Mark as applied immediately to prevent re-runs
            setInteriorColorApplied(true);

            // Check for URL color preferences first
            const urlPrefs = window.urlColorPreferences;

            // Update App state FIRST for both exterior and interior (before applying materials)
            // This prevents ColorButton from overriding with defaults
            if (urlPrefs?.exterior) {
                setSelectedExteriorColor({
                    colorCode: urlPrefs.exterior.colorCode,
                    name: urlPrefs.exterior.name,
                    color: urlPrefs.exterior.colorCode
                });
            }

            if (urlPrefs?.interior) {
                setSelectedInteriorColor({
                    colorCode: urlPrefs.interior.colorCode,
                    name: urlPrefs.interior.name,
                    color: urlPrefs.interior.colorCode
                });
            }

            // Small delay to ensure state updates propagate before material application
            addTrackedTimeout(() => {
                if (!isMountedRef.current) return;
                // Apply exterior color from URL if present
                if (urlPrefs?.exterior && materialChangeFunction) {

                    // Look up the metalness value from the color data
                    let metalness = 0.9; // Default fallback
                    if (selectedTrim && bmwX3Data[selectedTrim.carDataKey]) {
                        const carData = bmwX3Data[selectedTrim.carDataKey];
                        const colorMatch = carData.Exterior.material.color.find(
                            c => c.colorCode.toLowerCase() === urlPrefs.exterior.colorCode.toLowerCase()
                        );
                        if (colorMatch && colorMatch.metalness !== undefined) {
                            metalness = colorMatch.metalness;
                        }
                    }

                    const exteriorInfo = {
                        materialName: 'EXT_CARPAINT',
                        colorCode: urlPrefs.exterior.colorCode,
                        metalness: metalness,
                        materialType: 'exterior'
                    };
                    materialChangeFunction(exteriorInfo);
                }

                // Determine interior color: URL takes precedence over everything
                const interiorColorCode = urlPrefs?.interior?.colorCode || selectedInteriorColor?.colorCode || '#484848';

                // Handle plastic material color mapping for beige vs black/brown
                let plasticColorCode;
                if (interiorColorCode === '#D4C5A9') { // beige
                    plasticColorCode = '#D4C5A9';
                } else {
                    plasticColorCode = '#484848'; // black/brown/default
                }

                const forceInteriorMaterial = {
                    materials: [
                        { materialName: 'INT_LEATHER_PATTERN', colorCode: interiorColorCode },
                        { materialName: 'INT_LEATHER', colorCode: interiorColorCode },
                        { materialName: 'INT_STITCH', colorCode: interiorColorCode },
                        { materialName: 'INT_PLASTIC_COLOR_CHANGE', colorCode: plasticColorCode }
                    ],
                    materialType: 'interior'
                };

                // Apply interior materials
                materialChangeFunction(forceInteriorMaterial);
            }, 150); // Longer delay to ensure state updates first
        }
    }, [materialChangeFunction, modelLoaded, interiorColorApplied, selectedInteriorColor]);

    const handleMaterialChange = (materialInfo) => {
        if (materialChangeFunction) {
            materialChangeFunction(materialInfo);
        }
    };

    // Handle slider data from Scene component
    const handleSliderDataReady = useCallback((data) => {
        setSliderData(data);

        // Update color select text visibility based on hotspot
        if (data.activeHotspot === 'powerMeetsPrecision') {
            setShowColorSelectText(true);
        } else {
            setShowColorSelectText(false);
        }
    }, []);

    // Handle back navigation from scene to trim page
    const handleBackToTrim = () => {
        // Don't unmount Scene - just hide it and show trim page
        // Scene stays mounted so models stay loaded
        setShowTrim(true);

        // Reset interaction states for next session
        setUserInteracted(false);
        setShow360(false);
        setShowPointer(false);
        setPromptsComplete(false);
        setHeadlightsOn(false);

        // Reset view states to defaults
        setActiveView(0); // Back to exterior
        setInteriorMode(false); // Back to exterior mode
        setCameraAnimating(false);

        // Don't reset these - they'll be handled when user selects trim again:
        // - modelLoaded (stays true)
        // - bloomPrewarmingComplete (stays true)
        // - showScene (stays true - Scene remains mounted)
        // - selectedTrim (will update when user picks new trim)

        // Reset material function to force re-initialization
        setMaterialChangeFunction(null);

        // Reset slider data to defaults
        setSliderData({
            activeHotspot: 'none',
            cameraAnimating: false,
            interiorMode: false,
            sliderChangeHandler: null
        });

        // Reset heater toggle and color select text
        setIsHeaterToggleActive(false);
        setShowColorSelectText(false);
    };

    // Handle the sequence of showing 360 and pointer icons
    useEffect(() => {
        if (!isMountedRef.current) return;
        // Trigger sequence as soon as model and scene are ready AND user has entered configurator
        if (modelLoaded && bloomPrewarmingComplete && !userInteracted && showScene && !showTrim) {

            // Show 360 icon first
            setShow360(true);

            // After 3 seconds, switch to pointer
            const timer1 = addTrackedTimeout(() => {
                if (!isMountedRef.current) return;
                setShow360(false);
                setShowPointer(true);

                // After another 3 seconds, hide pointer and mark prompts as complete
                const timer2 = addTrackedTimeout(() => {
                    if (!isMountedRef.current) return;
                    setShowPointer(false);
                    setPromptsComplete(true);
                }, 3000);
            }, 3000);
        }
    }, [modelLoaded, bloomPrewarmingComplete, userInteracted, showScene, showTrim, addTrackedTimeout]);

    // Handle touch and click events to hide icons and text
    const handleInteraction = () => {
        if (!isMountedRef.current) return;
        setUserInteracted(true);
        setShow360(false);
        setShowPointer(false);
        // Mark prompts as complete after user interaction (with slight delay)
        addTrackedTimeout(() => {
            if (!isMountedRef.current) return;
            setPromptsComplete(true);
        }, 500);
        setShowColorSelectText(false); // Hide color select text on any interaction
    };
    // Preload models during initial loading screen
    useEffect(() => {
        // Dynamically import and preload GLB models
        const preloadModels = async () => {
            try {
                const { useGLTF } = await import('@react-three/drei');

                // Preload the main BMW model
                await useGLTF.preload('/models/BMW.glb');

                // Preload environment model
                await useGLTF.preload('/models/Environment.glb');

                setModelsPreloaded(true);
            } catch (error) {
                console.error('❌ Error preloading models:', error);
                // Still set as preloaded to not block the app
                setModelsPreloaded(true);
            }
        };

        preloadModels();
    }, []);

    // Show trim page after models are preloaded (and start scene initialization in background)
    useEffect(() => {
        if (!isMountedRef.current) return;
        if (modelsPreloaded) {
            const trimTimer = addTrackedTimeout(() => {
                if (!isMountedRef.current) return;
                setLoading(false);
                setShowTrim(true);
            }, 100);

            // Start scene initialization after a delay to let trim page animations run smoothly
            const sceneTimer = addTrackedTimeout(() => {
                if (!isMountedRef.current) return;
                setShowScene(true);
            }, 1500); // 1.5 second delay - enough time for trim page to render and animate smoothly
        }
    }, [modelsPreloaded, addTrackedTimeout]);

    const handleEnterClick = (selectedTrimData) => {
        if (!isMountedRef.current) return;

        // Clear any existing timeout
        if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
        }

        // Update trim selection
        setSelectedTrim(selectedTrimData);

        // Hide trim page to reveal already-loaded scene
        setShowTrim(false);

        // Scene should already be loaded from initial preload
        // Just verify it's ready
        const sceneAlreadyReady = showScene && modelLoadedRef.current && bloomPrewarmingCompleteRef.current;

        if (sceneAlreadyReady) {
            // Scene is already loaded - instant switch!
            loadingRef.current = false;
            setLoading(false);
        } else {
            // Scene still loading - show loading screen until ready
            setLoading(true);
            loadingRef.current = true;

            // Multiple safety checks for mobile devices with staggered timings
            addTrackedTimeout(checkAndHideLoading, 100);   // Quick check
            addTrackedTimeout(checkAndHideLoading, 300);   // Medium check
            addTrackedTimeout(checkAndHideLoading, 500);   // Slower check for mobile
            addTrackedTimeout(checkAndHideLoading, 1000);  // Final fallback for slow mobile devices

            // CRITICAL: Maximum timeout fallback for mobile devices (5 seconds)
            loadingTimeoutRef.current = setTimeout(() => {
                if (!isMountedRef.current) return;
                if (loadingRef.current) {
                    loadingRef.current = false;
                    setLoading(false);

                    // Triple force for stubborn mobile devices
                    requestAnimationFrame(() => {
                        if (isMountedRef.current) setLoading(false);
                    });
                    setTimeout(() => {
                        if (isMountedRef.current) setLoading(false);
                    }, 50);
                }
            }, 5000);
        }

        // Reset interaction states for the new session
        setUserInteracted(false); // Reset this to allow the 360/pointer sequence to trigger
        setShow360(false); // Start with these hidden
        setShowPointer(false);
        setPromptsComplete(false);
        setHeadlightsOn(false);
    };

    // Watch for scene becoming ready and hide loading screen (backup safety net)
    useEffect(() => {
        // Only hide loading if we're showing scene and both model and bloom are complete
        if (showScene && modelLoaded && bloomPrewarmingComplete && loading && !showTrim) {
            loadingRef.current = false;
            setLoading(false);

            // Force additional update for mobile devices
            requestAnimationFrame(() => {
                setLoading(false);
            });
        }
    }, [showScene, modelLoaded, bloomPrewarmingComplete, loading, showTrim]);

    // Always render scene in background once models start loading
    // Show Loading or Trim screens as overlays
    return (
        <>
            {/* Mobile-only gate - show blocking screen for desktop users */}
            {isMobile === false && <MobileOnlyScreen />}

            {/* Don't render anything until we know if it's mobile or not */}
            {isMobile === null && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900">
                    <div className="text-white">Detecting device...</div>
                </div>
            )}

            {/* Loading Screen Overlay - shown during initial load OR when waiting for scene */}
            {isMobile === true && loading && (
                <div className="fixed inset-0 z-50">
                    <LoadingScreen />
                </div>
            )}

            {/* Trim Selection Overlay - shown after loading is complete */}
            {isMobile === true && showTrim && (
                <div className="fixed inset-0 z-[100]">
                    <Suspense fallback={<LoadingScreen />}>
                        <Trim onEnterClick={handleEnterClick} />
                    </Suspense>
                </div>
            )}

            {/* Scene Container - always rendered once initialization starts */}
            {isMobile === true && showScene && (
                <div
                    className="w-full h-screen bg-gray-900 relative overflow-hidden"
                    onClick={handleInteraction}
                    onTouchStart={handleInteraction}
                >
                    {/* Show loading screen overlay until model is loaded AND bloom prewarming is complete */}
                    {(!modelLoaded || !bloomPrewarmingComplete) && <LoadingScreen />}

                    {/* Logo + Buttons Section - Only show after model is loaded AND bloom prewarming is complete */}
                    {modelLoaded && bloomPrewarmingComplete && (
                        <>
                            <div className="absolute top-0 left-0 w-full z-10">
                                <Logo
                                    carModel={selectedTrim ? selectedTrim.name : "Classic"}
                                    trimType={selectedTrim ? selectedTrim.name.toLowerCase() : "classic"}
                                    onBackClick={handleBackToTrim}
                                />
                                {!headlightsOn && (
                                    <div className="px-6 flex items-center justify-between">
                                        <Suspense fallback={null}>
                                            <ColorButton
                                                selectedTrim={selectedTrim}
                                                activeView={activeView}
                                                onMaterialChange={handleMaterialChange}
                                                bloomPrewarmingComplete={bloomPrewarmingComplete}
                                                selectedExteriorColor={selectedExteriorColor}
                                                selectedInteriorColor={selectedInteriorColor}
                                                onExteriorColorChange={setSelectedExteriorColor}
                                                onInteriorColorChange={setSelectedInteriorColor}
                                            />
                                            <ToggleButton
                                                onToggle={handleToggle}
                                                defaultActive={activeView}
                                                disabled={cameraAnimating}
                                            />
                                        </Suspense>
                                    </div>
                                )}
                                {/* Calipers Toggle - Show when Power Meets Precision hotspot is active */}
                                {/* {sliderData.activeHotspot === 'powerMeetsPrecision' && !sliderData.cameraAnimating && (
                                    <div className="fixed left-[5%] top-1/2 -translate-y-1/2 z-50">
                                        <CallipersToggle
                                            onToggle={handleCaliperToggle}
                                            activeHotspot={sliderData.activeHotspot}
                                            cameraAnimating={sliderData.cameraAnimating}
                                        />
                                    </div>
                                )} */}
                                {/* Center Text for Color Selection */}
                                {selectedTrim?.trimType === 'msport' && sliderData.activeHotspot === 'powerMeetsPrecision' && showColorSelectText && (
                                    <div className="fixed inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 40 }}>
                                        <div className="text-white text-xl tracking-normal opacity-90" style={{ textShadow: '0 2px 0.8px rgba(0, 0, 0, 0.25), 1px 2px 4px rgba(0, 0, 0, 0.25), 0 4px 4px rgba(0, 0, 0, 0.25)' }}>
                                            Choose your color
                                        </div>
                                    </div>
                                )}
                                {/* Callipers Toggle - only show for M Sport during Power Meets Precision hotspot */}
                                {selectedTrim?.trimType === 'msport' && (
                                    <div className="fixed right-8 top-[70%] -translate-y-1/2" style={{ zIndex: 1000 }}>
                                        <Suspense fallback={null}>
                                            <CallipersToggle
                                                onToggle={handleCaliperToggle}
                                                activeHotspot={sliderData.activeHotspot}
                                                cameraAnimating={sliderData.cameraAnimating}
                                            />
                                        </Suspense>
                                    </div>
                                )}
                            </div>

                            {/* Sliders component in the center */}
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="pointer-events-auto">
                                    <Suspense fallback={null}>
                                        <Sliders
                                            onSlideChange={(data) => {

                                                // Handle toggle changes for heat waves
                                                if (data.type === 'toggle') {
                                                    // Index 0 = SliderSelected (Exterior View), Index 1 = HeaterSelected (Interior View)
                                                    const isHeater = data.index === 1;
                                                    setIsHeaterToggleActive(isHeater);
                                                    trackHeaterToggle(isHeater);
                                                }

                                                // Pass the slider change to Scene's handler
                                                if (sliderData.sliderChangeHandler) {
                                                    sliderData.sliderChangeHandler(data);
                                                }
                                            }}
                                            activeHotspot={sliderData.activeHotspot}
                                            interiorMode={sliderData.interiorMode}
                                            cameraAnimating={sliderData.cameraAnimating}
                                        />
                                    </Suspense>
                                </div>
                            </div>

                            {/* <MainHotspot /> */}
                            {show360 && <ThreeSixty />}
                            {showPointer && <Pointer />}
                            <Suspense fallback={null}>
                                <Popup
                                    onTap={handleEnterClick}
                                    activeHotspot={sliderData.activeHotspot}
                                    onExpandChange={setIsPopupExpanded}
                                    selectedTrim={selectedTrim}
                                    selectedExteriorColor={selectedExteriorColor}
                                    selectedInteriorColor={selectedInteriorColor}
                                    showTrim={showTrim}
                                    hasEnteredConfigurator={showScene && !showTrim}
                                />
                            </Suspense>
                        </>
                    )}
                    {/* Scene */}
                    <Suspense fallback={<LoadingScreen />}>
                        <Scene
                            modelFile={selectedTrim?.modelFile || "BMW.glb"}
                            selectedTrim={selectedTrim?.name || "Sport - Classic BMW Design"}
                            selectedTrimData={selectedTrim || { trimType: 'classic', visibleNode: 'BASE_PARTS', hiddenNode: 'PRO_PARTS' }} // Pass full trim object for node visibility
                            onModelLoaded={handleModelLoaded}
                            onBloomPrewarmingComplete={handleBloomPrewarmingComplete}
                            onHeadlightsChange={setHeadlightsOn}
                            onMaterialChangeReady={handleMaterialChangeReady}
                            onScreenTextureUpdateReady={handleScreenTextureUpdateReady}
                            interiorMode={interiorMode}
                            setCameraAnimating={setCameraAnimating}
                            // Pass custom hotspot positions
                            hotspotCameraPositions={CUSTOM_HOTSPOT_POSITIONS}
                            onSliderDataReady={handleSliderDataReady}
                            isHeaterToggleActive={isHeaterToggleActive}
                            onBackClick={handleBackToTrim}
                            show360={show360}
                            showPointer={showPointer}
                            userInteracted={userInteracted}
                            promptsComplete={promptsComplete}
                            isPopupExpanded={isPopupExpanded}
                            modelLoaded={modelLoaded}
                            bloomPrewarmingComplete={bloomPrewarmingComplete}
                        />
                    </Suspense>
                </div>
            )}
        </>
    );
}
export default App
